import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menubar } from 'primereact/menubar';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import logo30Years from '../assets/logo30Years.svg';


const Layout = () => {
  const { user, logout, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toast = useRef(null);
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Logout Failed',
        detail: error.message,
      });
    }
  };

  const userMenuItems = [
    {
      label: 'Profile',
      icon: 'pi pi-user',
      command: () => {
        // Navigate to profile page when implemented
        toast.current.show({
          severity: 'info',
          summary: 'Profile',
          detail: 'Profile page coming soon',
        });
      },
    },
    {
      separator: true,
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: handleLogout,
    },
  ];

  const sidebarItems = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      command: () => navigate('/dashboard'),
      className: location.pathname === '/dashboard' ? 'active' : '',
    },
    {
      label: 'Documents',
      icon: 'pi pi-file',
      command: () => navigate('/documents'),
      className: location.pathname === '/documents' ? 'active' : '',
    },
    {
      label: 'Upload',
      icon: 'pi pi-upload',
      command: () => navigate('/upload'),
      className: location.pathname === '/upload' ? 'active' : '',
      visible: hasAnyRole(['ADMIN', 'EDITOR']),
    },
    {
      separator: true,
      visible: hasAnyRole(['ADMIN']),
    },
    {
      label: 'Admin Panel',
      icon: 'pi pi-cog',
      command: () => navigate('/admin'),
      className: location.pathname === '/admin' ? 'active' : '',
      visible: hasAnyRole(['ADMIN']),
    },
  ];

  const filteredSidebarItems = sidebarItems.filter(item => item.visible !== false);

  const handleToggleMenu = () => {
    if (window.innerWidth < 768) {
      setSidebarVisible(true);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  };

  const startContent = (
    <div className="flex align-items-center">
      <Button
        icon="pi pi-bars"
        className="p-button-text p-button-plain mr-3"
        onClick={handleToggleMenu}
        aria-label="Toggle menu"
      />
      <h1 className="m-0 text-xl font-semibold">LHRC Documents Verification</h1>
    </div>
  );

  const endContent = (
    <div className="flex align-items-center gap-3 mr-4">
      <span className="text-sm text-muted">
        {user?.firstName} {user?.lastName}
      </span>
      <Avatar
        image={`https://ui-avatars.com/api/?name=${user?.firstName}+${user?.lastName}&background=41A6D7&color=fff`}
        shape="circle"
        size="normal"
        className="cursor-pointer"
        onClick={(e) => userMenuRef.current.toggle(e)}
      />
      <Menu
        ref={userMenuRef}
        model={userMenuItems}
        popup
      />
    </div>
  );

  return (
    <div className="layout-container surface-ground min-h-screen flex">
      <Toast ref={toast} />
      
      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="w-15rem"
      >
        <div className="flex flex-column gap-2">
          {filteredSidebarItems.map((item, index) => {
            if (item.separator) {
              return <hr key={index} className="my-2" />;
            }
            return (
              <Button
                key={index}
                label={item.label}
                icon={item.icon}
                className={`p-button-text p-button-plain justify-start ${item.className}`}
                onClick={item.command}
              />
            );
          })}
        </div>
      </Sidebar>

      {/* Desktop Sidebar */}
      <div className={`hidden md:flex flex-column surface-section border-right-1 surface-border sidebar-container p-4 transition-all ${sidebarCollapsed ? 'w-3rem' : 'w-15rem'}`}>
        <div className={`flex align-items-center justify-content-between ml-4 mb-3 ${sidebarCollapsed ? 'justify-content-centre' : ''}`}>
          {!sidebarCollapsed && 
             <div >
              <img src={logo30Years} alt="Document Logo" style={{ width: '50px', height: '50px' }} />
            </div>}
          <Button
            icon={sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'}
            className="p-button-text p-button-sm"
            onClick={() => setSidebarCollapsed(prev => !prev)}
            aria-label="Collapse sidebar"
          />
        </div>
        <div className="flex flex-column gap-1 w-10">
          {filteredSidebarItems.map((item, index) => {
            if (item.separator) {
              return <hr key={index} className="my-2" />;
            }
            return (
              <Button
                key={index}
                label={sidebarCollapsed ? '' : item.label}
                icon={item.icon}
                className={`p-button-text p-button-plain text-align-start justify-start px-4 py-2 text-sm ${item.className}`}
                onClick={item.command}
                tooltip={sidebarCollapsed ? item.label : undefined}
                tooltipOptions={{ position: 'right' }}
                // outlined
                // style={{ p}}
              />
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-column min-h-screen">
        {/* Desktop Header */}
        <Menubar
          start={startContent}
          end={endContent}
          className="border-none border-bottom-1 surface-border top-menubar"
        />
        {/* Main Content */}
        <div className="main-content p-3 md:p-4 lg:p-5">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;